import {useCan} from '@/shared/hooks/use-permissions'
import {useConfirmDeleteActiveSubstance} from '@/modules/drug-catalog/substances/api'
import {SubstanceSummary} from '@/modules/drug-catalog/substances/components/active-substance-drawer/substance-summary'
import {SubstanceDrawerTabsList} from '@/modules/drug-catalog/substances/components/active-substance-drawer/tabs-list'
import {AboutTab} from '@/modules/drug-catalog/substances/components/active-substance-drawer/tabs/about'
import {HistoryTab} from '@/modules/drug-catalog/substances/components/active-substance-drawer/tabs/activity'
import {MedicationProfilesTab} from '@/modules/drug-catalog/substances/components/active-substance-drawer/tabs/medication-profiles'
import {TradeNamesTab} from '@/modules/drug-catalog/substances/components/active-substance-drawer/tabs/trade-names'
import {getActiveSubstanceDrawerId} from '@/modules/drug-catalog/substances/components/active-substance-drawer/util'
import {isSubstanceDeletable} from '@/modules/drug-catalog/substances/functions'
import {drawer} from '@cypro/ui/components'
import {DrawerBody, DrawerFooter, DrawerHeader} from '@cypro/ui/components'
import {DeleteResourceButton} from '@cypro/ui/components/ui/delete-resource-button'
import {LinkButton} from '@cypro/ui/components/ui/link-button'
import {Tooltip} from '@cypro/ui/components/ui/tooltip'
import {useActiveSubstanceDrugContext} from '@/shared/contexts/active-substance-drug'
import {toaster} from '@cypro/ui/toaster'
import {Drawer, Tabs} from '@chakra-ui/react'
import {useTranslate} from '@tolgee/react'

export function SubstanceDrawer() {
  const canManageCatalogue = useCan('catalogue.manage')
  const {t} = useTranslate('common')
  const {t: tSubstances} = useTranslate('substances')
  const {activeSubstanceDrug} = useActiveSubstanceDrugContext()
  const deleteSubstance = useConfirmDeleteActiveSubstance()

  async function onDelete() {
    try {
      await deleteSubstance({id: activeSubstanceDrug.id})
      const drawerId = getActiveSubstanceDrawerId(activeSubstanceDrug.id)
      drawer.close(drawerId)
      toaster.success({title: t('success'), description: t('resource_deleted_successfully')})
    } catch {}
  }

  const canDelete = isSubstanceDeletable(activeSubstanceDrug)
  const deleteBtn = <DeleteResourceButton onClick={onDelete} disabled={!canDelete} />

  return (
    <>
      <DrawerHeader>
        <Drawer.Title>{t('substance_details')}</Drawer.Title>
      </DrawerHeader>

      <DrawerBody>
        <SubstanceSummary />

        <Tabs.Root defaultValue="about" spaceY="5" lazyMount>
          <SubstanceDrawerTabsList />

          <AboutTab />
          <MedicationProfilesTab />
          <TradeNamesTab />
          <HistoryTab />
        </Tabs.Root>
      </DrawerBody>

      {canManageCatalogue && (
        <DrawerFooter display="flex" justifyContent="space-between">
          {/* // TODO: implement active substances v2 */}
          {!canDelete && <Tooltip content={tSubstances('cannot_delete_message', {tradeNames: 0})}>{deleteBtn}</Tooltip>}
          {canDelete && deleteBtn}
          <LinkButton
            to="/configuration/drug-catalog/active-substances/new"
            search={{substanceId: activeSubstanceDrug.id}}
            colorPalette="cyan"
            data-e2e="edit-substance"
          >
            {t('edit')}
          </LinkButton>
        </DrawerFooter>
      )}
    </>
  )
}
